import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import Layout from "@/components/Layout";
import CreateTopicDialog from "@/components/CreateTopicDialog";
import {
  MessageSquare,
  Users,
  Clock,
  Eye,
  Star,
  ChevronLeft,
  Pin,
  Lock,
  ThumbsUp,
  Share2,
  MoreHorizontal,
  Edit
} from "lucide-react";
import { communityApi } from "@/api";
import { useToast } from '@/hooks/use-toast';
import { useCommunityCategories } from "@/hooks/useCommunityCategories";

interface Topic {
  id?: string | number;
  title: string;
  category?: string;
  content?: string;
  updateTime?: string;
  isPinned?: boolean;
  isLocked?: boolean;
  isCollect?: boolean;
  isAuthor?: boolean;
  tags?: string;

  nickname?: string;
  userRole?: number;
  createTime?: string;
  collectCount?: number;
  commentCount?: number;
  readCount?: number;
  topicTypeId?: number | string;
}

interface Reply {
  topicId?: number | string;
  content?: string;

  id?: string;
  nickname?: string;
  createTime?: string;
  likeCount?: number;
  isAuthor?: boolean;
  isLike?: boolean;
}

const TopicDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [topic, setTopic] = useState<Topic | null>(null);
  const [replies, setReplies] = useState<Reply[]>([]);
  const [repliesNum, setRepliesNum] = useState<number>(0);
  const [replyContent, setReplyContent] = useState("");
  const [loading, setLoading] = useState(true);
  // 编辑话题状态
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  // 使用社区分类 Hook
  const { categories, isLoading: isCategoriesLoading } = useCommunityCategories();
  const { toast } = useToast();

  /**
   * 处理编辑话题
   */
  const handleEditTopic = () => {
    setIsEditDialogOpen(true);
  };

  /**
   * 编辑话题成功后的回调
   */
  const handleEditSuccess = () => {
    fetchTopicDetail();
  };

  const getCategoryInfo = (categoryId: string | number) => {
    return categories.find(cat => Number(cat.dictValue) === Number(categoryId)) || categories[0];
  };

  /** 用户角色1：普通用户 2：官方 */
  const getRoleColor = (role: number) => {
    switch (role) {
      case 1:
        return "bg-gray-100 text-gray-700";
      case 2:
        return "bg-blue-100 text-blue-700";
      default:
        return "bg-green-100 text-green-700";
    }
  };

  const handleReplySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyContent.trim()) return;

    try {
      const newReply: Reply = {
        topicId: id,
        content: replyContent,
      };
      const response = await communityApi.createTopicComment(newReply);

      if (response?.code === 200) {
        toast({
          title: "发布成功",
          description: "您的评论已成功发布",
        });
        setReplyContent("");
      } else {
        toast({
          title: "发布失败",
          description: response?.msg || "发布评论失败，请稍后重试",
          variant: "destructive",
        });
      }
      await fetchTopicComments();
    } catch (error) {
      console.error("Error creating reply:", error);
    }
  };

  /**
   * 获取话题详情
   */
  const fetchTopicDetail = async (getComments=true) => {
    try {
      const response = await communityApi.getTopicDetail(id);
      setTopic(response.data);
      if(getComments){
        await fetchTopicComments();
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching communities:", error);
    }
  };

  /**
   * 获取话题关联回复
   */
  const fetchTopicComments = async () => {
    try {
      const response = await communityApi.getTopicComment({ topicId: id });
      setReplies(response.rows);
      setRepliesNum(response.total || 0);
    } catch (error) {
      console.error("Error fetching communities:", error);
    }
  };

  /**
   * 处理回复点赞
   * @param reply
   */
  const handleReplyLike = async (reply: Reply) => {
    if(!reply.isLike){
      try {
        const response = await communityApi.likeTopicComment(reply.id);
        if (response?.code === 200) {
          toast({
            title: "点赞成功",
          });
          await fetchTopicComments()
        } else {
          toast({
            title: "点赞失败",
            description: response?.msg || "点赞失败，请稍后重试",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Error liking reply:", error);
      }
    }else {
      try {
        const response = await communityApi.cancelLikeTopicComment(reply.id);
        if (response?.code === 200) {
          toast({
            title: "取消点赞成功",
          });
          await fetchTopicComments()
        } else {
          toast({
            title: "取消点赞失败",
            description: response?.msg || "取消点赞失败，请稍后重试",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Error canceling like reply:", error);
      }
    }
  };

  /**
   * 处理收藏
   * @param topic
   */
  const handleCollect = async (topic: Topic) => {
    if(!topic.isCollect){
      try {
        const response = await communityApi.collectTopic(topic.id);
        if (response?.code === 200) {
          toast({
            title: "收藏成功",
          });
          await fetchTopicDetail(false)
        } else {
          toast({
            title: "收藏失败",
            description: response?.msg || "收藏失败，请稍后重试",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Error liking Collect:", error);
      }
    }else {
      try {
        const response = await communityApi.cancelCollectTopic(topic.id);
        if (response?.code === 200) {
          toast({
            title: "取消收藏成功",
          });
          await fetchTopicDetail(false)
        } else {
          toast({
            title: "取消收藏失败",
            description: response?.msg || "取消收藏失败，请稍后重试",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Error canceling Collect:", error);
      }
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchTopicDetail();
  }, [id]);

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center py-12">
            <div className="inline-block animate-pulse">加载中...</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!topic) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center py-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">话题不存在或已被删除</h2>
            <Button onClick={() => navigate("/community")}>
              <ChevronLeft className="mr-2 h-4 w-4" />
              返回社区
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  const categoryInfo = getCategoryInfo(topic.topicTypeId);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate("/community")}
          className="mb-6"
        >
          <ChevronLeft className="mr-2 h-4 w-4" />
          返回社区
        </Button>

        {/* Topic Header */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-2">
                {topic.isPinned && (
                  <Pin className="h-5 w-5 text-red-500" />
                )}
                {topic.isLocked && (
                  <Lock className="h-5 w-5 text-gray-500" />
                )}
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                  {topic.title}
                </h1>
              </div>
              <div className="flex space-x-2">
                <Button variant="ghost" size="icon" onClick={() => handleCollect(topic)}>
                  <Star className={topic.isCollect ? "h-4 w-4 fill-blue-400" : "h-4 w-4"}/>
                </Button>
                <Button variant="ghost" size="icon" disabled={true}>
                  <Share2 className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" disabled={true}>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
                {topic.isAuthor && (
                    <Button variant="ghost" size="icon" onClick={handleEditTopic}>
                      <Edit className="h-4 w-4" />
                    </Button>
                )}
              </div>
            </div>

            {/* Category and Tags */}
            <div className="flex flex-wrap gap-2 mb-6">
              <Badge className={categoryInfo.color}>
                {(() => {
                  const IconComponent = categoryInfo.icon;
                  return <IconComponent className="w-4 h-4" />;
                })()}
                <span className="ml-1">{categoryInfo.dictLabel}</span>
              </Badge>
              {topic?.tags?.replace(/，/g, ',')
                  .split(',')
                  .map((tag, idx) => (
                <Badge key={idx} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-gray-500">
              <div className="flex items-center space-x-1">
                <Users className="h-4 w-4" />
                <span>{topic.nickname}</span>
                <Badge className={`text-xs ${getRoleColor(topic.userRole)}`}>
                  {topic.userRole === 1 ? '用户' : '官方' }
                </Badge>
              </div>
              <div className="flex items-center space-x-1">
                <Clock className="h-4 w-4" />
                <span>{topic.createTime}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Eye className="h-4 w-4" />
                <span>{topic.readCount} 浏览</span>
              </div>
              <div className="flex items-center space-x-1">
                <MessageSquare className="h-4 w-4" />
                <span>{topic.commentCount} 回复</span>
              </div>
              <div className="flex items-center space-x-1">
                <Star className="h-4 w-4" />
                <span>{topic.collectCount} 收藏</span>
              </div>
            </div>

            {/* Author Info */}
            <div className="flex items-center space-x-4 mb-6 pb-6 border-b">
              <Avatar className="w-12 h-12">
                {/*<AvatarImage src={topic.avatar} />*/}
                <AvatarFallback>{topic.nickname.charAt(0)}</AvatarFallback>
              </Avatar>
              <div>
                <div className="flex items-center space-x-2">
                  <span className="font-medium">{topic.nickname}</span>
                  <Badge className={`text-xs ${getRoleColor(topic.userRole)}`}>
                    {topic.userRole === 1 ? '用户' : '官方' }
                  </Badge>
                </div>
                <p className="text-sm text-gray-500">
                  最后活跃: {topic.updateTime}
                </p>
              </div>
            </div>

            {/* Topic Content */}
            <div className="prose max-w-none">
              <div
                className="tiptap-content"
                dangerouslySetInnerHTML={{ __html: topic.content }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Reply Form */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>发表回复</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleReplySubmit} className="space-y-4">
              <Textarea
                placeholder="写下你的回复..."
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                className="min-h-[120px]"
              />
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Avatar className="w-8 h-8">
                    <AvatarFallback>我</AvatarFallback>
                  </Avatar>
                  <span className="text-sm text-gray-500">作为我回复</span>
                </div>
                <Button type="submit">
                  发布回复
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Replies List */}
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">
            {repliesNum} 个回复
          </h2>

          <div className="space-y-4">
            {replies.map((reply) => (
              <Card key={reply.id} className={`${reply.isAuthor ? "border-blue-200" : ""}`}>
                <CardContent className="p-6">
                  <div className="flex space-x-4">
                    <Avatar className="w-10 h-10 flex-shrink-0">
                      {/*<AvatarImage src={reply.avatar} />*/}
                      <AvatarFallback>{reply.nickname.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium">{reply.nickname}</span>
                          {/*<Badge className={`text-xs ${getRoleColor(reply.author.role)}`}>*/}
                          {/*  {reply.author.role}*/}
                          {/*</Badge>*/}
                          {reply.isAuthor && (
                            <Badge variant="outline" className="text-xs text-blue-600 border-blue-200">
                              作者
                            </Badge>
                          )}
                        </div>
                        <span className="text-sm text-gray-500">{reply.createTime}</span>
                      </div>

                      <div className="text-gray-700 mb-4">
                        {reply.content}
                      </div>

                      <div className="flex items-center space-x-4 text-sm">
                        <Button variant="ghost" size="sm" onClick={() => handleReplyLike(reply)}>
                          {reply.isLike && (
                              <ThumbsUp className="mr-1 h-3 w-3 fill-red-500 text-red-500" />
                          )}
                          {!reply.isLike && (
                              <ThumbsUp className="mr-1 h-3 w-3" />
                          )}
                          <span className={reply.isLike ? "text-red-500" : ""}>{reply.likeCount}</span>
                        </Button>
                        {/*<Button variant="ghost" size="sm" disabled={true}>*/}
                        {/*  回复*/}
                        {/*</Button>*/}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* 编辑话题对话框 */}
      {topic && (
        <CreateTopicDialog
          isOpen={isEditDialogOpen}
          onClose={() => setIsEditDialogOpen(false)}
          onSuccess={handleEditSuccess}
          isEditMode={true}
          topicData={{
            id: topic.id,
            title: topic.title,
            topicTypeId: topic.topicTypeId,
            content: topic.content,
            tags: topic.tags
          }}
        />
      )}
    </Layout>
  );
};

export default TopicDetail;